import React from 'react';
import { School, Award, Building2, GraduationCap, RefreshCw } from 'lucide-react';
import { BackButton } from './shared';
import { SchoolDirectoryPanel } from './SchoolDirectoryPanel';
import { directorySummary, TOTAL_INSTITUTIONS } from '../lib/schoolDirectory';

// The whole-country directory, on its own route.
//
// "Find Schools" on the homepage banner, and the hero's "Search Schools" tile,
// used to land on /schools — which is the High Schools door: titled "High
// School in Jamaica", leading with 231 secondary schools. A banner promising
// 1,650+ therefore delivered a page that looked like it had 231, with the full
// search six sections further down. The claim was backed but the landing was
// not, which is the same failure as the old "1,000+" copy.
//
// This page leads with the search and nothing else, so the first thing a
// visitor sees matches what they were promised. /schools keeps its own listing
// and its rankings for people who specifically want a high school.
const LEVEL_LINKS = [
  { level: 'Early Childhood', page: 'primary', icon: School, label: 'Infant & early childhood' },
  { level: 'Primary', page: 'primary', icon: Building2, label: 'Prep & primary' },
  { level: 'Secondary', page: 'schools', icon: Award, label: 'High schools' },
  { level: 'Tertiary', page: 'tertiary', icon: GraduationCap, label: 'Universities & colleges' },
  { level: 'Special', page: 'special-education', icon: RefreshCw, label: 'Special education' },
];

export const AllSchoolsPage = ({ setPage, goBack }) => {
  // Door pages link in with their level preselected, e.g.
  // /all-schools?level=Secondary. Read from the URL so the filtered view can
  // be shared and survives a refresh.
  const requested =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('level') || ''
      : '';
  const initialLevel = LEVEL_LINKS.some((l) => l.level === requested) ? requested : '';

  return (
  <div className="w-full">
    <BackButton goBack={goBack} setPage={setPage} />

    <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-700 via-brand-800 to-brand-900 text-white shadow-hero mb-8">
      <div className="absolute inset-0 bg-hero-sheen pointer-events-none" aria-hidden="true" />
      <div className="relative p-6 sm:p-8 md:p-10">
        <span
          className="inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur mb-5"
          aria-hidden="true"
        >
          <School className="w-6 h-6 md:w-7 md:h-7" />
        </span>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold leading-[1.15] max-w-3xl">
          Every School in Jamaica
        </h1>
        <p className="mt-3 text-[15px] sm:text-base md:text-lg text-white/85 leading-relaxed max-w-2xl">
          All {TOTAL_INSTITUTIONS.toLocaleString()} institutions in the Ministry of Education
          directory — early childhood through university, in all 14 parishes.
        </p>

        <dl className="mt-8 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-5 max-w-3xl">
          {LEVEL_LINKS.map((l) => (
            <div key={l.level}>
              <dt className="sr-only">{l.level}</dt>
              <dd>
                <span className="block text-2xl sm:text-3xl font-extrabold tabular-nums leading-none">
                  {(directorySummary.byLevel[l.level] || 0).toLocaleString()}
                </span>
                <span className="block mt-1.5 text-[13px] font-medium text-brand-200">
                  {l.level}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>

    <SchoolDirectoryPanel
      levelOptions={['Early Childhood', 'Primary', 'Secondary', 'Tertiary', 'Special']}
      title="Search the directory"
      blurb="Filter by education level, parish, or public and independent. Every record carries its address, telephone and Ministry e-mail."
      initial={15}
      initialLevel={initialLevel}
    />

    {/* The ranked listing lives on the High Schools door, not here. Rank and
        CSEC/CAPE score exist only for secondary schools — 19 of the 231 carry a
        2024 score today — so a "ranked" section on a page covering infant
        schools and universities was promising an ordering that most of the
        1,654 records can never have. */}
    <section className="mb-8">
      <h2 className="text-xl md:text-2xl font-extrabold text-ink mb-1">Looking for a high school?</h2>
      {/* Deliberately no count. The High Schools guide lists 231 schools from
          the rankings datasets; the search above holds 253 secondary records
          from the Ministry directory. Quoting either number here would promise
          the destination has the other one's coverage. */}
      {/* CSEC only, not "CSEC and CAPE". The index behind the ranking measures
          one thing: the share of a school's Grade 11 cohort passing five or
          more CSEC subjects including Maths and English. There is no CAPE
          metric in it. */}
      <p className="text-[15px] text-ink-muted mb-5">
        The High Schools guide ranks them on CSEC passes.
      </p>
      <button
        onClick={() => setPage('schools')}
        className="inline-flex items-center gap-2.5 px-5 min-h-[44px] rounded-2xl font-semibold text-white bg-brand-700 shadow-sm transition-all duration-200 hover:bg-brand-800 hover:shadow-lg active:scale-[0.98]"
      >
        <Award className="w-5 h-5" aria-hidden="true" />
        Open the ranked high school directory
      </button>
    </section>

    <section className="mb-8">
      <h2 className="text-xl md:text-2xl font-extrabold text-ink mb-1">Browse by stage instead</h2>
      <p className="text-[15px] text-ink-muted mb-5">
        Each stage has its own guide — entry requirements, exams, costs and what to expect.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {LEVEL_LINKS.map((l) => (
          <button
            key={l.label}
            onClick={() => setPage(l.page)}
            className="group flex items-center gap-3.5 p-4 min-h-[44px] rounded-2xl text-left bg-canvas-raised ring-1 ring-hairline shadow-sm transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:ring-brand-200 active:scale-[0.99]"
          >
            <span className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-xl bg-brand-50 ring-1 ring-brand-100 transition-transform duration-200 group-hover:scale-110">
              <l.icon className="w-5 h-5 text-brand-700" aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className="block font-bold text-[15px] text-ink leading-tight">{l.label}</span>
              <span className="block text-sm text-ink-muted mt-0.5">
                {(directorySummary.byLevel[l.level] || 0).toLocaleString()} listed
              </span>
            </span>
          </button>
        ))}
      </div>
    </section>
    </div>
  );
};

export default AllSchoolsPage;
